import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, Home, Lightbulb, Building2, Hammer, MapPin, BookOpen } from 'lucide-react';

const navItems = [
  { path: '/', label: 'Start', icon: Home },
  { path: '/tippspiele', label: 'Tippspiele', icon: Lightbulb },
  { path: '/immobilien', label: 'Immobilien', icon: Building2 },
  { path: '/haus-und-bau', label: 'Haus & Bau', icon: Hammer },
  { path: '/suedtirol', label: 'Südtirol', icon: MapPin },
  { path: '/ratgeber', label: 'Ratgeber', icon: BookOpen },
];

const partnerLinks = [
  { name: 'Hafner EC', url: 'https://hafner-ec.com', description: 'Bauunternehmen Meran' },
  { name: 'amadeus.immo', url: 'https://amadeus.immo', description: 'Immobilienmakler' },
  { name: 'sanieren.it', url: 'https://sanieren.it', description: 'Sanierungsportal' },
  { name: 'Kohlbrenner', url: 'https://kohlbrenner.com', description: 'Urlaub in Südtirol' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="main-header">
      <div className="container-wide">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 rounded-lg bg-[#0284C7] flex items-center justify-center text-white font-bold text-lg font-[var(--font-heading)]">
              T
            </div>
            <span className="font-[var(--font-heading)] font-bold text-xl text-[#0F172A] hidden sm:block group-hover:text-[#0284C7] transition-colors duration-200">
              tippspiel.it
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive 
                      ? 'bg-[#0284C7]/10 text-[#0284C7]' 
                      : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                  }`}
                  data-testid={`nav-link-${item.path.replace(/\//g, '') || 'home'}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors"
            data-testid="mobile-menu-button"
            aria-label="Menü öffnen"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav 
            className="lg:hidden py-4 border-t border-[#E2E8F0]"
            data-testid="mobile-nav"
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 ${
                    isActive 
                      ? 'bg-[#0284C7]/10 text-[#0284C7]' 
                      : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                  }`}
                  data-testid={`mobile-nav-link-${item.path.replace(/\//g, '') || 'home'}`}
                >
                  <Icon size={20} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  const [contactEmail, setContactEmail] = useState(null);

  // Spam protection: only render email client-side
  useEffect(() => {
    setContactEmail('info@hafner-ec.com');
  }, []);

  return (
    <footer className="bg-[#0F172A] text-white py-16" data-testid="main-footer">
      <div className="container-wide">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#0284C7] flex items-center justify-center text-white font-bold text-lg">
                T
              </div>
              <span className="font-[var(--font-heading)] font-bold text-xl">
                tippspiel.it
              </span>
            </div>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-4">
              Das Tippspiel für bessere Entscheidungen rund um Wohnen, Bauen und Immobilien in Südtirol.
            </p>
            <p className="text-[#64748B] text-xs">
              Ein Projekt der HAFNER EC GmbH, Meran
            </p>
          </div>

          {/* Tippspiele */}
          <div>
            <h4 className="font-[var(--font-heading)] font-semibold text-base mb-4">Tippspiele</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/tippspiele/was-kostet-dieses-haus" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Was kostet dieses Haus?
                </Link>
              </li>
              <li>
                <Link to="/tippspiele/neubau-oder-sanierung" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Neubau oder Sanierung?
                </Link>
              </li>
              <li>
                <Link to="/immobilien/wohnung-kaufen-oder-mieten" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Kaufen oder Mieten?
                </Link>
              </li>
              <li>
                <Link to="/haus-und-bau/was-kostet-eine-sanierung" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Was kostet eine Sanierung?
                </Link>
              </li>
            </ul>
          </div>

          {/* Ratgeber */}
          <div>
            <h4 className="font-[var(--font-heading)] font-semibold text-base mb-4">Ratgeber</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/ratgeber/hausbau-in-suedtirol" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Hausbau in Südtirol
                </Link>
              </li>
              <li>
                <Link to="/haus-und-bau/baufirma-meran" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Baufirma in Meran finden
                </Link>
              </li>
              <li>
                <Link to="/suedtirol/wohnen-in-suedtirol" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Wohnen in Südtirol
                </Link>
              </li>
            </ul>
          </div>

          {/* Partner & Rechtliches */}
          <div>
            <h4 className="font-[var(--font-heading)] font-semibold text-base mb-4">Partner</h4>
            <ul className="space-y-2 mb-6">
              {partnerLinks.map((partner) => (
                <li key={partner.url}>
                  <a 
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#94A3B8] hover:text-white text-sm transition-colors"
                    title={partner.description}
                  >
                    {partner.name}
                  </a>
                </li>
              ))}
            </ul>
            
            <h4 className="font-[var(--font-heading)] font-semibold text-base mb-4">Rechtliches</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/impressum" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Impressum
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-[#94A3B8] hover:text-white text-sm transition-colors">
                  Datenschutz
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-[#1E293B]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-[#64748B] text-sm">
              © {new Date().getFullYear()} tippspiel.it – Ein Projekt der HAFNER EC GmbH, Meran
            </p>
            <div className="flex items-center gap-4 text-[#64748B] text-xs">
              <span>MwSt.-Nr.: IT 02706840218</span>
              <span className="hidden sm:inline">|</span>
{contactEmail && (
                <a 
                  href={`mailto:${contactEmail}`}
                  className="hover:text-white transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = `mailto:${contactEmail}`;
                  }}
                >
                  {contactEmail}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-16 md:pt-20">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
